import React, { useState, useEffect } from 'react';
import './KnightMove.css'; // Optional: for styling, create this file if needed
import { Modal } from 'antd';

const BOARD_SIZE = 7;

// All possible knight moves (row, col)
const KNIGHT_MOVES = [
  [2, 1], [1, 2], [-1, 2], [-2, 1],
  [-2, -1], [-1, -2], [1, -2], [2, -1],
];

function isValid(row: number, col: number) {
  return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE;
}

const KnightMove: React.FC = () => {
  // board: 0 = empty, >0 = move number
  const [board, setBoard] = useState<number[][]>(Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(0)));
  const [moveCount, setMoveCount] = useState(0);
  const [currentPos, setCurrentPos] = useState<[number, number] | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Compute valid moves from current position
  const getValidMoves = () => {
    if (currentPos === null) return Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(true)); // All allowed on first move
    const [row, col] = currentPos;
    const valid = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(false));
    for (const [dr, dc] of KNIGHT_MOVES) {
      const nr = row + dr, nc = col + dc;
      if (isValid(nr, nc) && board[nr][nc] === 0) {
        valid[nr][nc] = true;
      }
    }
    return valid;
  };

  const validMoves = getValidMoves();

  // Check if any moves left
  const hasMovesLeft = () => {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (validMoves[r][c]) return true;
      }
    }
    return false;
  };

  // Helper: check if the board is fully filled
  const isBoardFull = () => {
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c] === 0) return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (moveCount === 0) return; // Don't check on initial render
    if (isBoardFull()) {
      setGameOver(true);
      setShowModal(false);
    } else if (!hasMovesLeft()) {
      setGameOver(true);
      setShowModal(true);
    }
  }, [board]);

  const handleClick = (row: number, col: number) => {
    if (gameOver) return;
    if (board[row][col] !== 0) return;
    if (currentPos !== null && !validMoves[row][col]) return;

    const newBoard = board.map(arr => arr.slice());
    newBoard[row][col] = moveCount + 1;
    setBoard(newBoard);
    setMoveCount(moveCount + 1);
    setCurrentPos([row, col]);
    // Game-over logic is now handled in useEffect
  };

  const handleReset = () => {
    setBoard(Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(0)));
    setMoveCount(0);
    setCurrentPos(null);
    setGameOver(false);
    setShowModal(false);
  };

  return (
    <div style={{ textAlign: 'center', marginTop: 24 }}>
      <h2>Knight Move Game (7x7)</h2>
      <div className="knight-move-board">
        {board.map((rowArr, rowIdx) => (
          <div key={rowIdx} className="knight-move-row">
            {rowArr.map((cell, colIdx) => {
              const isCurrent = currentPos && currentPos[0] === rowIdx && currentPos[1] === colIdx;
              const canClick = !gameOver && cell === 0 && (currentPos === null || validMoves[rowIdx][colIdx]);
              const classNames = [
                'knight-move-cell',
                cell ? 'filled' : '',
                canClick ? 'valid' : '',
                isCurrent ? 'current' : '',
              ].filter(Boolean).join(' ');
              return (
                <button
                  key={colIdx}
                  onClick={() => handleClick(rowIdx, colIdx)}
                  disabled={!canClick}
                  className={classNames}
                >
                  {cell ? cell : ''}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div>
        <button className="knight-move-reset-btn" onClick={handleReset}>Reset Game</button>
      </div>
      {gameOver && (
        <div className="knight-move-status">
          Game Over! {moveCount === BOARD_SIZE * BOARD_SIZE ? 'You filled all squares!' : 'No more valid moves.'}
        </div>
      )}
      <Modal
        open={showModal}
        onOk={handleReset}
        onCancel={() => setShowModal(false)}
        title="Game Over"
        okText="Restart"
        cancelText="Close"
      >
        <p>Game over, no more possible moves.</p>
      </Modal>
    </div>
  );
};

export default KnightMove;
